<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-lg-8 offset-lg-2 col-md-10 offset-md-1 col-sm-12">

        <div class="text-end mb-3">
          <button
            v-if="counterAvailability == 1"
            @click="changeAvailability(2)"
            type="button"
            class="btn btn-danger"
          >
            Make Unavailable
          </button>
          <button
            v-else
            type="button"
            @click="changeAvailability(1)"
            class="btn btn-success"
          >
            Make Available
          </button>
          <button type="button" @click="logout()" class="btn btn-primary ms-2">
            Logout
          </button>
        </div>

        <div class="card shadow-sm mb-4">
          <div class="card-body">
            <h5 class="text-center m-4 fw-bold text-secondary" style="font-size: 30px;">
              {{ counterName }}
            </h5>
            <p class="text-center text-muted mb-0">OP Outsource Token (not system-generated)</p>

            <div class="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 m-4">
              <div class="text-center text-sm-start">
                <span class="fw-bold" style="font-size: 30px;">
                  Current Token:
                  <strong class="text-success">{{ currentToken || '—' }}</strong>
                </span>
                <div v-if="nextTokenNumber" class="mt-2">
                  <span class="fw-bold text-secondary">
                    Next in sequence: {{ getNextTokenDisplay() }}
                  </span>
                </div>
                <div v-if="lastCalledToken" class="mt-2">
                  <span class="fw-bold">
                    Last called: {{ lastCalledToken }}
                    <span v-if="lastCallTypeLabel" class="badge bg-secondary ms-1">{{ lastCallTypeLabel }}</span>
                  </span>
                </div>
              </div>

              <div
                v-if="counterAvailability == 1"
                class="d-flex flex-row flex-wrap gap-2 mt-3 mt-sm-0 justify-content-center"
              >
                <button
                  class="btn btn-danger btn-lg"
                  v-if="currentToken"
                  @click="recallToken()"
                  :disabled="actionLoading"
                >
                  Recall
                </button>
                <button
                  class="btn btn-success btn-lg"
                  @click="callNextToken()"
                  :disabled="actionLoading"
                >
                  <span v-if="callingNext" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  Call Next
                </button>
              </div>
            </div>

            <div
              v-if="counterAvailability == 1"
              class="token-call-row d-flex flex-wrap justify-content-center align-items-end gap-2 px-3 pb-2"
            >
              <div class="token-number-field">
                <label class="form-label small mb-1">Token number</label>
                <input
                  :value="randomTokenNumber != null ? randomTokenNumber : ''"
                  type="text"
                  inputmode="none"
                  readonly
                  class="form-control token-number-input text-center"
                  :class="{ 'is-invalid': tokenNumberError }"
                  placeholder="Enter token number"
                />
                <div v-if="tokenNumberError" class="invalid-feedback d-block">
                  {{ tokenNumberError }}
                </div>
              </div>
              <div class="token-call-btn">
                <label class="form-label small mb-1 d-block invisible user-select-none">&nbsp;</label>
                <button
                  class="btn btn-info btn-lg text-white"
                  @click="callRandomToken()"
                  :disabled="actionLoading"
                >
                  Call
                </button>
              </div>
            </div>

            <div
              v-if="counterAvailability == 1"
              class="token-keypad px-3 pb-3"
            >
              <div class="token-keypad-grid">
                <button
                  v-for="digit in ['1', '2', '3', '4', '5', '6', '7', '8', '9']"
                  :key="'d-' + digit"
                  type="button"
                  class="btn btn-outline-secondary token-key"
                  :disabled="actionLoading"
                  @click="keypadPress(digit)"
                >
                  {{ digit }}
                </button>
                <button
                  type="button"
                  class="btn btn-outline-danger token-key"
                  :disabled="actionLoading"
                  @click="keypadClear()"
                >
                  C
                </button>
                <button
                  type="button"
                  class="btn btn-outline-secondary token-key"
                  :disabled="actionLoading"
                  @click="keypadPress('0')"
                >
                  0
                </button>
                <button
                  type="button"
                  class="btn btn-outline-warning token-key"
                  :disabled="actionLoading"
                  @click="keypadBackspace()"
                >
                  ⌫
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="card shadow-sm mb-4">
          <div class="card-body">
            <div class="row text-center">
              <div class="col-6">
                <h5 class="fw-bold" style="font-size: large;">
                  Today's calls:
                  <strong class="text-success">{{ todayCallsCount }}</strong>
                </h5>
              </div>
              <div class="col-6">
                <h5 class="fw-bold" style="font-size: large;">
                  Next sequential:
                  <strong class="text-primary">{{ getNextTokenDisplay() }}</strong>
                </h5>
              </div>
            </div>
          </div>
        </div>

        <div class="card shadow-sm mb-4">
          <div class="card-body">
            <h5 class="text-center mb-4 fw-bold text-primary">Today's token calls</h5>

            <div v-if="callHistory.length > 0" class="table-responsive">
              <table class="table table-bordered align-middle mb-0">
                <thead style="background-color: #007bff; color: white;">
                  <tr class="text-center">
                    <th width="20%">Token</th>
                    <th width="25%">Time</th>
                    <th width="15%">Actions</th>
                  </tr>
                </thead>
                <tbody class="table-light">
                  <tr
                    v-for="call in callHistory"
                    :key="call.id"
                    class="text-center fw-semibold"
                    :class="{ 'table-warning': call.id === currentOutsourceCallId }"
                  >
                    <td>
                      <span class="badge bg-primary fs-6 px-3 py-2">{{ call.token }}</span>
                    </td>
                    <td>{{ call.created_at | formatDateTime }}</td>
                    <td>
                      <button
                        class="btn btn-sm btn-outline-danger"
                        @click="recallToken(call)"
                        :disabled="actionLoading"
                      >
                        Recall
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else class="text-center text-muted py-4">
              <p class="mb-0">No tokens called today</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      token: '',
      counterName: '',
      counterAvailability: 1,
      currentToken: '',
      currentOutsourceCallId: null,
      lastCalledToken: '',
      lastCallTypeLabel: '',
      nextTokenNumber: 1,
      tokenPrefix: '',
      todayCallsCount: 0,
      callHistory: [],
      randomTokenNumber: null,
      tokenNumberError: '',
      actionLoading: false,
      callingNext: false,
      refreshInterval: null,
    };
  },

  mounted() {
    this.guardWrongTokenType();
    this.fetchState();
    this.fetchCallHistory();
    this.startAutoRefresh();
  },

  beforeDestroy() {
    this.stopAutoRefresh();
  },

  methods: {
    guardWrongTokenType() {
      const stored = this.getStoredCounter();
      if (stored && stored.token_management_type != null && Number(stored.token_management_type) !== 3) {
        this.redirectToLoginForTokenTypeChange();
      }
    },

    redirectToLoginForTokenTypeChange() {
      this.stopAutoRefresh();
      localStorage.removeItem('medicino_counter');
      this.$swal('', 'Token management type was updated. Please login again.', 'warning');
      this.$router.replace({ name: 'login' });
    },

    keypadPress(digit) {
      this.tokenNumberError = '';
      const current = this.randomTokenNumber != null ? String(this.randomTokenNumber) : '';
      if (current.length >= 8) return;
      const next = current === '0' ? digit : current + digit;
      this.randomTokenNumber = Number(next);
    },

    keypadBackspace() {
      this.tokenNumberError = '';
      if (this.randomTokenNumber == null) return;
      const current = String(this.randomTokenNumber);
      if (current.length <= 1) {
        this.randomTokenNumber = null;
        return;
      }
      this.randomTokenNumber = Number(current.slice(0, -1));
    },

    keypadClear() {
      this.tokenNumberError = '';
      this.randomTokenNumber = null;
    },

    getStoredCounter() {
      if (!localStorage.getItem('medicino_counter')) return null;
      try {
        return JSON.parse(localStorage.getItem('medicino_counter'));
      } catch (e) {
        return null;
      }
    },

    getToken() {
      const medicino_counter = this.getStoredCounter();
      if (medicino_counter && medicino_counter.token) {
        this.token = medicino_counter.token;
        this.counterName = medicino_counter.counter_name || medicino_counter.name || 'Counter';
        return true;
      }
      this.$swal('', 'Session expired, please login again', 'error');
      this.$router.push({ name: 'login' });
      return false;
    },

    authHeaders(extra = {}) {
      return {
        Accept: 'application/json',
        Authorization: 'Bearer ' + this.token,
        ...extra,
      };
    },

    callTypeLabel(type) {
      const map = { 1: 'Next', 2: 'Random', 3: 'Recall' };
      return map[Number(type)] || '—';
    },

    getNextTokenDisplay() {
      if (!this.nextTokenNumber) return '';
      return this.tokenPrefix
        ? `${this.tokenPrefix}${this.nextTokenNumber}`
        : this.nextTokenNumber;
    },

    deriveTokenPrefix(token) {
      if (!token) return '';
      const match = String(token).match(/^([^\d]+)(\d+)$/);
      return match ? match[1] : '';
    },

    resolveTokenPrefix(data) {
      const fromApi = data.prefix ?? data.token_prefix;
      if (fromApi != null && fromApi !== '') {
        return String(fromApi);
      }
      return (
        this.deriveTokenPrefix(data.current_token?.token) ||
        this.deriveTokenPrefix(data.last_called_token?.token)
      );
    },

    applyIndexData(data) {
      this.counterAvailability = Number(data.counter_availability ?? 1);
      this.nextTokenNumber = data.next_token_number ?? 1;
      this.tokenPrefix = this.resolveTokenPrefix(data);
      this.todayCallsCount = data.today_calls_count ?? 0;

      if (data.current_token) {
        this.currentToken = data.current_token.token;
        this.currentOutsourceCallId = data.current_token.outsource_call_id;
      } else {
        this.currentToken = '';
        this.currentOutsourceCallId = null;
      }

      if (data.last_called_token) {
        this.lastCalledToken = data.last_called_token.token;
        this.lastCallTypeLabel = this.callTypeLabel(data.last_called_token.call_type);
      } else {
        this.lastCalledToken = '';
        this.lastCallTypeLabel = '';
      }
    },

    handleApiError(error, fallback = 'Something went wrong') {
      if (error.response) {
        if (error.response.status === 403) {
          this.redirectToLoginForTokenTypeChange();
          return;
        }

        const msg = error.response.data?.message;
        if (typeof msg === 'string') {
          this.$swal('', msg, 'error');
        } else if (msg && typeof msg === 'object') {
          const first = Object.values(msg)[0];
          this.$swal('', Array.isArray(first) ? first[0] : String(first), 'error');
        } else {
          this.$swal('', fallback, 'error');
        }
      } else {
        this.$swal('', fallback, 'error');
      }
    },

    startAutoRefresh() {
      this.refreshInterval = setInterval(() => {
        this.fetchState(false);
      }, 20000);
    },

    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },

    fetchState(showLoader = true) {
      if (!this.getToken()) return;
      const url = 'counter/outsource/tokens';
      this.$axios
        .get(url, { headers: this.authHeaders() })
        .then((res) => {
          const data = res.data.data;
          if (
            data?.token_management_type != null &&
            Number(data.token_management_type) !== 3
          ) {
            this.redirectToLoginForTokenTypeChange();
            return;
          }
          this.applyIndexData(data);
        })
        .catch((err) => {
          if (showLoader) this.handleApiError(err, 'Failed to load outsource tokens.');
          else if (err.response?.status === 403) this.handleApiError(err);
        });
    },

    fetchCallHistory() {
      if (!this.getToken()) return;
      const url = 'counter/outsource/callHistory';
      this.$axios
        .get(url, { headers: this.authHeaders() })
        .then((res) => {
          this.callHistory = res.data.data?.calls || [];
        })
        .catch((err) => {
          if (err.response?.status === 403) this.handleApiError(err);
          else console.log(err);
        });
    },

    refreshAfterCall() {
      this.fetchState(false);
      this.fetchCallHistory();
    },

    callNextToken() {
      if (!this.getToken()) return;
      this.actionLoading = true;
      this.callingNext = true;
      const url = 'counter/outsource/callNextToken';
      this.$axios
        .get(url, { headers: this.authHeaders() })
        .then((res) => {
          const data = res.data.data;
          this.$swal('', res.data.message, 'success');
          if (data) {
            this.currentToken = data.token;
            this.currentOutsourceCallId = data.outsource_call_id;
            this.nextTokenNumber = data.next_token_number ?? this.nextTokenNumber;
          }
          this.refreshAfterCall();
        })
        .catch((err) => this.handleApiError(err, 'Failed to call next token.'))
        .finally(() => {
          this.actionLoading = false;
          this.callingNext = false;
        });
    },

    callRandomToken() {
      if (!this.getToken()) return;

      const num = Number(this.randomTokenNumber);
      if (!this.randomTokenNumber || !Number.isInteger(num) || num < 1) {
        this.tokenNumberError = 'Please enter a token number.';
        this.$swal('', this.tokenNumberError, 'warning');
        return;
      }

      this.tokenNumberError = '';
      this.actionLoading = true;

      const formData = new FormData();
      formData.append('token_number', num);

      const url = 'counter/outsource/callRandomToken';
      this.$axios
        .post(url, formData, { headers: this.authHeaders() })
        .then((res) => {
          const data = res.data.data;
          this.$swal('', res.data.message, 'success');
          if (data) {
            this.currentToken = data.token;
            this.currentOutsourceCallId = data.outsource_call_id;
            this.nextTokenNumber = data.next_token_number ?? this.nextTokenNumber;
          }
          this.randomTokenNumber = null;
          this.refreshAfterCall();
        })
        .catch((err) => this.handleApiError(err, 'Failed to call token.'))
        .finally(() => {
          this.actionLoading = false;
        });
    },

    recallToken(call) {
      if (!this.getToken()) return;
      this.actionLoading = true;

      const formData = new FormData();
      if (call && call.id) {
        formData.append('outsource_call_id', call.id);
      } else if (call && call.token_number) {
        formData.append('token_number', call.token_number);
      }

      const url = 'counter/outsource/recallToken';
      this.$axios
        .post(url, formData, { headers: this.authHeaders() })
        .then((res) => {
          const data = res.data.data;
          this.$swal('', res.data.message, 'success');
          if (data) {
            this.currentToken = data.token;
            this.currentOutsourceCallId = data.outsource_call_id;
          }
          this.refreshAfterCall();
        })
        .catch((err) => this.handleApiError(err, 'Failed to recall token.'))
        .finally(() => {
          this.actionLoading = false;
        });
    },

    changeAvailability(status) {
      if (!this.getToken()) return;
      const url = 'counter/outsource/changeAvailability?status=' + status;
      this.$axios
        .get(url, { headers: this.authHeaders() })
        .then((res) => {
          this.$swal('', res.data.message, 'success');
          this.fetchState(false);
          if (status === 2) {
            this.currentToken = '';
            this.currentOutsourceCallId = null;
          }
        })
        .catch((err) => this.handleApiError(err, 'Failed to update availability.'));
    },

    logout() {
      if (!this.getToken()) return;
      const url = 'counter/logout';
      this.$axios
        .get(url, { headers: this.authHeaders() })
        .then(() => {
          localStorage.removeItem('medicino_counter');
          this.$swal('', 'You have been successfully logged out', 'success');
          this.$router.push({ name: 'login' });
        })
        .catch((err) => this.handleApiError(err));
    },
  },
};
</script>

<style scoped>
.token-call-row {
  width: 100%;
  max-width: 280px;
  margin-left: auto;
  margin-right: auto;
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px !important;
  align-items: end !important;
}

.token-number-field {
  width: 100%;
  max-width: none;
}

.token-number-input,
.token-call-btn .btn {
  min-height: 64px;
  height: 64px;
  font-size: 1.15rem;
}

.token-number-input {
  caret-color: transparent;
  background-color: #fff;
}

.token-call-btn .btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1.25rem;
  white-space: nowrap;
}

.token-keypad {
  max-width: 280px;
  margin: 0 auto;
}

.token-keypad-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.token-key {
  min-height: 52px;
  font-size: 1.25rem;
  font-weight: 600;
  padding: 0.4rem 0.5rem;
}

@media (min-width: 992px) {
  .token-call-row {
    max-width: 320px;
  }

  .token-number-field {
    width: 100%;
  }

  .token-keypad {
    max-width: 320px;
  }

  .token-key {
    min-height: 48px;
  }
}
</style>
